import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { integrationsAPI, IntegrationStatus } from "../../services/api/api";

interface ProviderConfig {
  provider: "forge" | "tpc";
  name: string;
  description: string;
  tokenLabel: string;
  helpUrl: string;
  helpText: string;
  connect: (token: string) => Promise<IntegrationStatus>;
}

const PROVIDERS: ProviderConfig[] = [
  {
    provider: "forge",
    name: "Focus: Forge",
    description:
      "Pull your Forge Organizations (clients) and Projects into Income.",
    tokenLabel: "Forge Personal Access Token",
    helpUrl: "https://focusforge.theportlandcompany.com/settings/api",
    helpText:
      "Create a read token in Forge → Settings → API / Personal Access Tokens.",
    connect: (t) => integrationsAPI.connectForge(t),
  },
  {
    provider: "tpc",
    name: "TPC Account Center",
    description:
      "Link your TPC account to import your recurring & open invoices as Income.",
    tokenLabel: "TPC API Key",
    helpUrl:
      "https://myaccount.theportlandcompany.com/dashboard/settings/api-keys",
    helpText: "Create an API key in TPC Account Center → Settings → API Keys.",
    connect: (t) => integrationsAPI.connectTpc(t),
  },
];

const IntegrationsSettings: React.FC = () => {
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatuses(await integrationsAPI.list());
    } catch {
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const statusFor = (provider: string) =>
    statuses.find((s) => s.provider === provider);

  const handleConnect = async (cfg: ProviderConfig) => {
    const token = (tokens[cfg.provider] || "").trim();
    if (!token) {
      setErrors((e) => ({ ...e, [cfg.provider]: "Enter a token first." }));
      return;
    }
    setBusy(cfg.provider);
    setErrors((e) => ({ ...e, [cfg.provider]: "" }));
    try {
      await cfg.connect(token);
      setTokens((t) => ({ ...t, [cfg.provider]: "" }));
      await refresh();
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [cfg.provider]:
          err instanceof Error ? err.message : "Failed to connect",
      }));
    } finally {
      setBusy(null);
    }
  };

  const handleSyncTpc = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await integrationsAPI.syncTpc();
      const parts: string[] = [];
      if (r.imported) parts.push(`${r.imported} added`);
      if (r.updated) parts.push(`${r.updated} updated`);
      setSyncMsg(
        parts.length
          ? `Imported ${r.total} invoice(s): ${parts.join(", ")}.`
          : `No recurring or open invoices to import.`
      );
    } catch (err) {
      setSyncMsg(
        err instanceof Error ? err.message : "Failed to sync TPC income"
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async (provider: string) => {
    setBusy(provider);
    try {
      await integrationsAPI.disconnect(provider);
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {PROVIDERS.map((cfg, idx) => {
        const status = statusFor(cfg.provider);
        const connected = Boolean(status);
        const accountLabel = status?.accountLabel || status?.account_label;
        return (
          <React.Fragment key={cfg.provider}>
            {idx > 0 && <Divider />}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {cfg.name}
                </Typography>
                {connected && (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleIcon />}
                    label="Connected"
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {cfg.description}
              </Typography>

              {connected ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {accountLabel || "Linked"}
                    </Typography>
                    {cfg.provider === "tpc" && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={syncing}
                        onClick={handleSyncTpc}
                      >
                        {syncing ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          "Sync income now"
                        )}
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={busy === cfg.provider}
                      onClick={() => handleDisconnect(cfg.provider)}
                    >
                      Disconnect
                    </Button>
                  </Box>
                  {cfg.provider === "tpc" && syncMsg && (
                    <Alert
                      severity={
                        syncMsg.startsWith("Imported") ||
                        syncMsg.startsWith("No recurring")
                          ? "success"
                          : "error"
                      }
                      sx={{ mt: 1 }}
                    >
                      {syncMsg}
                    </Alert>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      label={cfg.tokenLabel}
                      value={tokens[cfg.provider] || ""}
                      onChange={(e) =>
                        setTokens((t) => ({
                          ...t,
                          [cfg.provider]: e.target.value,
                        }))
                      }
                    />
                    <Button
                      variant="contained"
                      disabled={busy === cfg.provider}
                      onClick={() => handleConnect(cfg)}
                      sx={{ whiteSpace: "nowrap", minWidth: 110 }}
                    >
                      {busy === cfg.provider ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "Connect"
                      )}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {cfg.helpText}{" "}
                    <Link
                      href={cfg.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open {cfg.name}
                    </Link>
                  </Typography>
                  {errors[cfg.provider] && (
                    <Alert severity="error" sx={{ mt: 0.5 }}>
                      {errors[cfg.provider]}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default IntegrationsSettings;
